import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject as RxSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CardComponent } from '../../../ui/card/card.component';
import { LoaderComponent } from '../../../ui/loader/loader.component';
import { QuizHeaderComponent } from '../../../ui/views/quiz-header/quiz-header.component';
import { PieComponent } from '../../../../shared/charts/pie/pie.component';
import { BackButtonComponent } from '../../../ui/back-button/back-button.component';
import { DurationPipe } from '../../../../shared/pipes/duration.pipe';
import { CanDirective } from '../../../../core/directives/can.directive';
import { ProfessorStudentSessionDto } from '../../../../core/services/professor.service';
import {
  StudentTestService,
  SessionFullDto,
} from '../../../../core/services/student-test.service';
import {
  SessionChartData,
  SessionResultMapperService,
} from '../../../../core/services/utils/session-result-mapper.service';


@Component({
  selector: 'app-session-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CardComponent,
    LoaderComponent,
    QuizHeaderComponent,
    PieComponent,
    BackButtonComponent,
    DurationPipe,
    CanDirective,
  ],
  templateUrl: './session-detail.component.html',
  styleUrl: './session-detail.component.css',
})
export class SessionDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private studentTestService = inject(StudentTestService);
  private mapper = inject(SessionResultMapperService);
  private translate = inject(TranslateService);
  private destroy$ = new RxSubject<void>();

  session: ProfessorStudentSessionDto | null = null;

  /**
   * Temps écoulé entre l'ouverture de la copie et sa clôture, en secondes.
   *
   * <p><b>À ne pas confondre avec le temps actif</b>, qui ne compte que ce que
   * l'élève a passé devant sa copie. Les deux figuraient côte à côte sans
   * être nommés : « démarré à 14 h 29, terminé à 15 h 40, durée 5 min 18 »
   * se lisait comme une erreur de calcul, alors que ce sont deux mesures
   * différentes — et leur écart dit quelque chose : une copie laissée ouverte,
   * ou close par l'échéance.</p>
   *
   * <p><c>null</c> tant que la copie n'est pas close : l'écart avec
   * « maintenant » grandirait à chaque cycle de détection.</p>
   */
  get dureeEcouleeSecondes(): number | null {
    const debut = this.session?.startedAt;
    const fin = this.session?.completedAt;
    if (!debut || !fin) return null;

    const secondes = (new Date(fin).getTime() - new Date(debut).getTime()) / 1000;
    return Number.isFinite(secondes) && secondes >= 0 ? Math.round(secondes) : null;
  }
  isLoading = false;
  hasError = false;
  errorMessage = '';
  isLTR = false;
  chartData: SessionChartData[] = [];

  ngOnInit(): void {
    this.isLTR = this.translate.currentLang !== 'ar';
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => {
        this.isLTR = e.lang !== 'ar';
        if (this.session) this.buildChartData();
      });

    const sessionId = Number(this.route.snapshot.paramMap.get('id'));
    if (!sessionId || sessionId <= 0) {
      this.handleError(this.translate.instant('ERRORS.INVALID_SESSION'));
      return;
    }

    // Use router state for base info (student name, test title, etc.) while full detail loads
    const navState: { session?: ProfessorStudentSessionDto } = window.history.state ?? {};
    if (navState.session) {
      this.session = navState.session;
      this.buildChartData();
    } else {
      this.isLoading = true;
    }

    // Always load full detail from the same endpoint the student page uses
    this.loadFullDetail(sessionId);
  }

  /**
   * Calls GET /student-tests/sessions/{id} — the same endpoint that powers
   * the student result page. Returns questionDetails, metadata, and accurate scores.
   */
  private loadFullDetail(sessionId: number): void {
    this.studentTestService
      .getSessionById(sessionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const raw: SessionFullDto | undefined = res?.data;
          if (!raw) {
            if (!this.session) {
              this.handleError(this.translate.instant('ERRORS.SESSION_NOT_FOUND'));
            }
            this.isLoading = false;
            return;
          }

          console.log('[session-detail] raw from student endpoint:', JSON.stringify(raw, null, 2));

          const counts  = this.mapper.computeAnswerCounts(raw);
          const cheating = this.mapper.extractCheatingStats(raw);

          const resolvedPercentage =
            raw.percentageScore != null ? raw.percentageScore
            : raw.percentage    != null ? raw.percentage
            : raw.score         != null ? raw.score
            : null;

          // Build a safe update: only non-null/non-undefined values overwrite existing data
          const enriched: Partial<ProfessorStudentSessionDto> = {};
          const candidates: Record<string, any> = {
            percentageScore:     resolvedPercentage,
            percentage:          resolvedPercentage,
            score:               raw.score            ?? null,
            passed:              raw.passed           ?? null,
            completedAt:         raw.completedAt      ?? null,
            timeSpentSeconds:    raw.timeSpentSeconds ?? null,
            totalPoints:         raw.totalPoints      ?? null,
            earnedPoints:        raw.earnedPoints     ?? null,
            ...counts,
            ...cheating,
          };

          for (const [key, val] of Object.entries(candidates)) {
            if (val !== null && val !== undefined) {
              (enriched as any)[key] = val;
            }
          }

          if (this.session) {
            this.session = { ...this.session, ...enriched } as ProfessorStudentSessionDto;
          } else {
            // No router state — construct session from raw response alone
            this.session = {
              id:                sessionId,
              studentId:         raw.studentId         ?? '',
              studentFullName:   raw['studentFullName'] ?? raw['student']?.firstName ?? '',
              studentEmail:      raw['studentEmail']    ?? raw['student']?.email ?? '',
              testId:            raw.testId             ?? 0,
              testTitle:         raw.testTitle          ?? raw['test']?.title ?? '—',
              testMode:          raw.testMode           ?? '',
              subjectName:       raw.subjectName        ?? raw['subject']?.name ?? '—',
              learningPathTitle: raw['learningPathTitle'] ?? '',
              status:            raw.status             ?? '',
              startedAt:         raw.startedAt          ?? '',
              attemptNumber:     raw.attemptNumber       ?? 0,
              ...enriched,
            } as ProfessorStudentSessionDto;
          }

          this.buildChartData();
          this.cdr.markForCheck();
          this.isLoading = false;
        },
        error: (err) => {
          console.warn('[session-detail] student endpoint failed:', err?.message);
          if (!this.session) {
            this.handleError(err?.message ?? this.translate.instant('ERRORS.LOAD_SESSION_ERROR'));
          }
          this.isLoading = false;
        },
      });
  }

  private buildChartData(): void {
    if (!this.session) return;
    const correct    = this.session.correctAnswers    ?? 0;
    const wrong      = this.session.wrongAnswers      ?? 0;
    const total      = this.session.totalQuestions    ?? 0;
    const unanswered = this.session.unansweredQuestions ?? Math.max(0, total - correct - wrong);

    this.translate
      .get(['CHART.CORRECT_ANSWERS', 'CHART.WRONG_ANSWERS', 'CHART.UNANSWERED'])
      .subscribe((t) => {
        this.chartData = this.mapper.buildPieChartData([
          { count: correct,    label: t['CHART.CORRECT_ANSWERS'], fill: '#14D520' },
          { count: wrong,      label: t['CHART.WRONG_ANSWERS'],   fill: '#d50b3e' },
          { count: unanswered, label: t['CHART.UNANSWERED'],      fill: '#9e9e9e' },
        ]);
        this.cdr.markForCheck();
      });
  }

  getScoreColor(): string {
    const p = this.session?.percentageScore ?? this.session?.percentage;
    return this.mapper.getScoreColorClass(p);
  }

  /**
   * Ouvre le rapport de diagnostic de l'élève, sur la matière de cette session.
   *
   * <p>Remplace le téléchargement de l'ancien PDF de session — un document
   * produit par le serveur, dans une mise en page qui n'était plus celle du
   * produit, et dont l'arabe sortait en « ????? » faute de police dans le
   * conteneur. Le rapport de diagnostic a sa propre impression, et c'est lui
   * que l'écran doit montrer.</p>
   *
   * <p>La matière de la session préselectionne son onglet : c'est d'elle qu'on
   * vient parler. Les trois boutons de langue n'ont plus lieu d'être — la page
   * suit la langue de l'application.</p>
   *
   * <p>Navigation dans le même onglet, comme depuis la liste des élèves : le
   * rapport revient par `history.back()`, il ne laisse donc personne en
   * chemin.</p>
   *
   * <p>Ce qu'on y perd, et il faut le savoir : l'ancien document descendait
   * question par question, le rapport de diagnostic s'arrête à la compétence.</p>
   */
  openDiagnosticReport(): void {
    const eleve = this.session?.studentId;
    if (!eleve) return;

    const matiere = this.session?.subjectId;
    void this.router.navigate(['/diagnostic-report/students', eleve], {
      queryParams: matiere ? { subject: matiere } : {},
    });
  }

  /**
   * Retour à la liste d'où l'on vient.
   *
   * <p>La destination était l'espace professeur, en dur. Le même écran sert
   * désormais l'administration : un administrateur s'y retrouvait renvoyé dans
   * une coquille que son rôle n'ouvre pas, et la garde le rejetait. L'URL
   * courante dit d'où l'on vient — c'est la seule source fiable, l'historique
   * du navigateur pouvant venir d'ailleurs.</p>
   */
  goBack(): void {
    const base = this.router.url.startsWith('/admin/')
      ? '/admin/student-following'
      : '/teacher/my-students';

    this.router.navigate([base]);
  }

  private handleError(msg: string): void {
    this.hasError = true;
    this.errorMessage = msg;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
